import { motion } from "framer-motion";
import { useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useRecentDonors } from "@/hooks/useRecentDonors";
import { useToast } from "@/hooks/use-toast";

export const DonorFeed = () => {
  const { toast } = useToast();
  const { data: donorsData, isLoading, error, isError } = useRecentDonors();

  const donors = donorsData?.donors || [];

  // Auto-scroll functionality - removed since we're not using infinite scroll anymore

  // Show error toast if API fails
  useEffect(() => {
    if (isError && error) {
      toast({
        title: "Unable to load recent donors",
        description: "Showing cached information. Please try again later.",
        variant: "destructive",
      });
    }
  }, [isError, error, toast]);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // No need to extend donors array since we're removing infinite scroll

  if (isLoading) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="py-20 px-4"
      >
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="font-sans font-normal tracking-tight text-3xl md:text-4xl text-foreground mb-4">
              Recent Supporters ❤️
            </h2>
            <p className="text-lg text-muted-foreground font-sans">
              Join these amazing people making a difference
            </p>
          </motion.div>

          {/* Desktop: Horizontal skeleton */}
          <div className="hidden md:block">
            <div className="flex gap-6">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="flex-shrink-0 w-80">
                  <div className="bg-background rounded-xl p-6 shadow-soft border border-border h-full">
                    <div className="flex items-start gap-4">
                      <Skeleton className="w-12 h-12 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-4 w-16" />
                        </div>
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-3/4" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: Vertical skeleton */}
          <div className="md:hidden space-y-4">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-background rounded-xl p-4 shadow-soft border border-border">
                <div className="flex items-start gap-3">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-3 w-12" />
                    </div>
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>
    );
  }

  if (donors.length === 0) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="py-20 px-4"
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="font-serif font-medium tracking-wide mb-4">
            Recent Supporters ❤️
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Be the first to support this important cause!
          </p>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="relative pt-20 px-4 border-t border-border overflow-hidden bg-primary"
    >
      
      <div className="relative z-10 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16 space-y-4"
        >
          <h2 className="font-sans font-normal tracking-tight text-3xl md:text-4xl text-white">
            Recent Supporters
          </h2>
          <p className="text-base text-white/80 font-sans font-light max-w-3xl mx-auto leading-relaxed">
            Join these people making a difference in baby formula safety
            {donorsData?.source === 'cache' && ' (showing cached data)'}
          </p>
        </motion.div>

        {/* Desktop: Horizontal scroll */}
        <div className="hidden md:block -mx-4">
          <div 
            className="overflow-x-auto scrollbar-hide pb-20"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex gap-6 w-max pl-4 pr-4">
              {donors.map((donor, index) => (
                <motion.div
                  key={donor.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="flex-shrink-0 w-48 h-48"
                >
                  <div className="relative w-full h-full rounded-lg group">
                    {/* Glass effect background */}
                    <div className="absolute inset-0 bg-white/20 backdrop-blur-xl backdrop-saturate-150 border border-white/30 group-hover:bg-white/30 transition-all duration-500 rounded-lg"></div>
                    
                    {/* Content */}
                    <div className="relative z-10 p-4 h-full flex flex-col justify-between text-center">
                      {/* Name at top */}
                      <div>
                        <h4 className="text-white font-light text-lg tracking-tight">
                          {donor.name}
                        </h4>
                      </div>
                      
                      {/* Amount in center - large and white */}
                      <div className="flex-1 flex items-center justify-center">
                        <span className="text-white font-medium text-4xl tracking-tight">
                          {formatAmount(donor.amount)}
                        </span>
                      </div>
                      
                    </div>
                  </div>
                </motion.div>
               ))}
            </div>
          </div>
        </div>

        {/* Mobile: Horizontal scroll */}
        <div className="md:hidden -mx-4">
          <div 
            className="overflow-x-auto scrollbar-hide pb-20"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div 
              className="flex gap-4 w-max pl-4 pr-4"
              style={{ 
                display: 'flex',
                gap: '1rem',
                width: 'max-content'
              }}
            >
              {donors.slice(0, 8).map((donor, index) => (
                <motion.div
                  key={donor.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="flex-shrink-0 w-40 h-40"
                >
                  <div className="relative w-full h-full rounded-2xl group">
                    {/* Glass effect background */}
                    <div className="absolute inset-0 bg-white/20 backdrop-blur-xl backdrop-saturate-150 border border-white/30 group-hover:bg-white/30 transition-all duration-500 rounded-lg"></div>
                    
                    {/* Content */}
                    <div className="relative z-10 p-4 h-full flex flex-col justify-between text-center">
                      {/* Name at top */}
                      <div>
                        <h4 className="text-white font-light text-sm tracking-tight">
                          {donor.name}
                        </h4>
                      </div>
                      
                      {/* Amount in center - large and white */}
                      <div className="flex-1 flex items-center justify-center">
                        <span className="text-white font-light text-xl tracking-tight">
                          {formatAmount(donor.amount)}
                        </span>
                      </div>
                      
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};